import { normalizeEvent } from '@solink/vd-data-layer/api-clients/shared/eventUtils';
import { type EventState, type EventStatePayload } from '@solinkcorp/api-types';
import {
  type CHCameraId,
  type CustomerId,
  type EventId,
  type SiteId,
  type VerifEyeGroupId,
} from '@solinkcorp/callhome';
import settings from '@solinkcorp/settings';
import { type AudioMergingOptions, type VoiceType } from 'components/AudioControls/audioOptions';
import { type Except } from 'type-fest';
import { type VideoDiscoverySite } from 'utils/types';
import { type IEvent } from './PlatformApi';

export type SortDirection = 'ASC' | 'DESC';

// VerifEye4's list sort. Array order is the sort precedence: the three priority keys lead so the
// most urgent work surfaces first, and the "Sort By" dropdown still drives the `created_at` (the
// VerifEye queue time) tiebreaker: 'DESC' for Newest first, 'ASC' for Oldest first (the default).
//
// `alarmSitePriority` ascends because MC ranks 1 as High (see HIGH_RISK_SITE_PRIORITY), so ASC
// puts high-risk sites on top. Keys are written exactly as MC expects them: `meta.`-prefixed for
// the verification's own namespace, bare otherwise, the same convention `filter` follows below.
function buildVerifEye4Sort(
  createdAtDirection: SortDirection = 'ASC',
): Array<Record<string, SortDirection>> {
  return [
    { priority: 'DESC' },
    { 'meta.alarmSitePriority': 'ASC' },
    { 'meta.alarmMonitoringModePriority': 'ASC' },
    { created_at: createdAtDirection },
  ];
}

export interface SnoozeBase {
  snoozeFor: number;
  snoozedAt: string;
}
export interface SnoozeCamera extends SnoozeBase {
  cameraId: CHCameraId;
}

export interface SnoozeCamerasResponse {
  cameraId: CHCameraId;
  metadata: { snoozeDurationSec: number; snoozedAt: string };
}

export interface SignedAudioFile {
  name: string;
  hashedFilename: string;
  url: string;
  signedUrl: string;
  lastModified?: Date;
}

export interface SavedAudioFileResponse {
  name: string;
  audio: string;
  url: string;
  success: boolean;
  message: string;
}

export interface VerificationProps {
  eventFlowId?: string;
  // The verification's event type ('alarm' | 'access' | 'agent'), which the verification page
  // pairs with the event's own `subtype` to decide whether Stay Mode applies. See
  // `isAlarmEventType`. Kept in step with the richer `VerificationProps` in useVerificationEvents.
  category?: string;
  state?: 'OPEN' | 'IN_PROGRESS' | 'CLOSED';
  verifier?: string;
  node_data?: {
    current?: { nodeId?: string; state?: string; data?: unknown };
    previous?: { nodeId?: string; state?: string; data?: unknown };
  };
  dataExtensions?: {
    meta?: {
      // The site's alarm monitoring mode, which is what selects the Stay Mode event flow. Left as
      // a plain string rather than the `AlarmMonitoringMode` union so this module does not import
      // `utils/AlarmUtil`, which imports `clients`. MC's `NO_MONITORING_MODE` sentinel arrives
      // here too. See `VerificationProps` in useVerificationEvents.ts for the richer typing.
      alarmMonitoringMode?: string;
    };
  };
  [key: string]: unknown;
}

export class MonitorCenterApi {
  async getOpenAlarmEvents(
    jwt: string,
    filter?: Record<string, unknown> | undefined,
    verifEyeIds?: VerifEyeGroupId[],
  ): Promise<{ results: IEvent[] } | undefined> {
    try {
      const body: { filter: Record<string, unknown> | undefined; verifEyeIds?: string[] } = {
        filter,
      };

      if (verifEyeIds && verifEyeIds.length > 0) {
        body.verifEyeIds = verifEyeIds;
      }

      // Sort events by oldest to newest
      const response = await fetch(
        `${settings.monitorCenterHost}/alarms?sortBy=startTime&sortDirection=asc`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify(body),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to get open alarm events: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      throw new Error(String(err));
    }
  }

  async getVerificationEvents({
    jwt,
    filter,
    verifEyeIds,
    eventTypes,
    createdAtDirection,
    searchString,
    verifyState,
    verifier,
    limit,
    includeExpired,
  }: {
    jwt: string;
    filter?: Record<string, unknown>;
    verifEyeIds?: VerifEyeGroupId[];
    eventTypes?: string[];
    createdAtDirection?: SortDirection;
    searchString?: string;
    verifyState?: string | string[];
    verifier?: (string | null)[];
    limit?: number;
    includeExpired?: boolean;
  }): Promise<{ results: { event: IEvent; props?: Record<string, unknown> }[] } | undefined> {
    try {
      const stateArray = Array.isArray(verifyState)
        ? verifyState
        : verifyState
          ? [verifyState]
          : undefined;

      // MC's /verifications endpoint matches filter keys against the underlying
      // event document, so prefix each one with `event.` (e.g. `siteId` ->
      // `event.siteId`). The transform is generic; any future filter keys are
      // prefixed automatically. Keys already targeting the verification's `meta`
      // namespace (e.g. `meta.alarmSiteLabel`) are passed through unchanged,
      // matching how `sort` addresses those same fields.
      const prefixedFilter = filter
        ? Object.fromEntries(
            Object.entries(filter).map(([key, value]) => [
              key.startsWith('meta.') ? key : `event.${key}`,
              value,
            ]),
          )
        : undefined;

      const body: {
        filter: Record<string, unknown> | undefined;
        verifEyeIds?: string[];
        priority?: string[];
        category?: string[];
        sort?: Array<Record<string, SortDirection>>;
        searchString: string;
        state?: string[];
        verifier?: (string | null)[];
        limit?: number;
        includeExpired?: boolean;
      } = {
        filter: prefixedFilter,
        priority: [],
        category: eventTypes ?? [],
        sort: buildVerifEye4Sort(createdAtDirection),
        searchString: searchString ?? '',
        ...(stateArray && stateArray.length > 0 && { state: stateArray }),
        ...(verifier && { verifier }),
        ...(limit !== undefined && { limit }),
        ...(includeExpired !== undefined && { includeExpired }),
      };

      if (verifEyeIds && verifEyeIds.length > 0) {
        body.verifEyeIds = verifEyeIds;
      }

      const response = await fetch(`${settings.monitorCenterHost}/verifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to get open verification events: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw new Error(String(err));
    }
  }

  async getCustomerAudioFiles(jwt: string, customerId: CustomerId): Promise<SignedAudioFile[]> {
    try {
      const response = await fetch(
        `${settings.monitorCenterHost}/customer/${customerId}/text-to-speech`,
        {
          method: 'GET',
          headers: this.getDefaultHeaders(jwt),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to get text-to-speech files: ${response.statusText}`);
      }

      const data = await response.json();
      return data.files || [];
    } catch (err) {
      throw new Error(String(err));
    }
  }

  async saveGeneratedAudioFile(
    jwt: string,
    customerId: CustomerId,
    name: string,
    audioBlob: Blob,
    options?: AudioMergingOptions,
  ): Promise<SavedAudioFileResponse> {
    try {
      const url = `${settings.monitorCenterHost}/customer/${customerId}/text-to-speech`;

      const arrayBuffer = await audioBlob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);
      const binaryString = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
      const base64Audio = btoa(binaryString);

      const payload: { name: string; audio: string } & Partial<AudioMergingOptions> = {
        name,
        audio: base64Audio,
      };

      if (options?.soundUrl && options?.soundPosition?.length) {
        payload.soundUrl = options.soundUrl;
        payload.soundPosition = options.soundPosition;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getDefaultHeaders(jwt),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to create text-to-speech file: ${response.statusText}`);
      }

      return await response.json();
    } catch (err) {
      throw new Error(String(err));
    }
  }

  async deleteCustomerAudioFile(
    jwt: string,
    customerId: CustomerId,
    hashedFilename: string,
  ): Promise<void> {
    try {
      const response = await fetch(
        `${settings.monitorCenterHost}/customer/${customerId}/text-to-speech/${hashedFilename}`,
        {
          method: 'DELETE',
          headers: this.getDefaultHeaders(jwt),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to delete text-to-speech file: ${response.statusText}`);
      }
    } catch (err) {
      throw new Error(String(err));
    }
  }

  async generateAudioFromText(
    jwt: string,
    title: string,
    text: string,
    voice: VoiceType,
  ): Promise<Blob> {
    try {
      const url = `${settings.monitorCenterHost}/text-to-speech`;
      const payload = { title, text, voice };

      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getDefaultHeaders(jwt),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error('TTS API Error:', {
          status: response.status,
          statusText: response.statusText,
          url,
          headers: Object.fromEntries(response.headers.entries()),
        });

        if (response.status === 403) {
          throw new Error('Authentication failed.');
        }

        if (response.status === 404) {
          throw new Error('Text-to-speech service not found.');
        }

        throw new Error(
          `Failed to generate text-to-speech preview: ${response.status} ${response.statusText}`,
        );
      }

      const contentType = response.headers.get('content-type') || '';
      const contentLength = response.headers.get('content-length');

      // If response is too small, it might be an error message
      if (contentLength && parseInt(contentLength) < 1000) {
        const textResponse = await response.text();
        throw new Error(`Invalid audio response: ${textResponse.substring(0, 200)}`);
      }

      if (contentType.includes('audio/')) {
        const base64Audio = await response.text();
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return new Blob([bytes], { type: 'audio/mpeg' });
      }

      throw new Error('Expected audio response, got: ' + contentType);
    } catch (err) {
      throw new Error(String(err));
    }
  }

  private async snoozeRequest(
    method: 'GET' | 'POST' | 'DELETE',
    jwt: string,
    siteId: SiteId,
    body?: Except<SnoozeCamera, 'snoozedAt'>[],
  ): Promise<SnoozeCamera[] | void> {
    try {
      const options: RequestInit = {
        method,
        headers: this.getDefaultHeaders(jwt),
      };
      if (method === 'POST' && body) {
        options.body = JSON.stringify(body);
      }
      const response = await fetch(
        `${settings.monitorCenterHost}/sites/${siteId}/snoozeCameras/`,
        options,
      );

      if (!response.ok) {
        throw new Error(`Failed to ${method.toLowerCase()} snooze cameras: ${response.statusText}`);
      }

      // Only parse JSON for GET requests
      if (method === 'GET') {
        const responseData = await response.json();
        const snoozedCameras = responseData?.snoozedCameras;

        if (!snoozedCameras || !Array.isArray(snoozedCameras)) {
          return [];
        }

        return snoozedCameras.map((scr: SnoozeCamerasResponse) => ({
          cameraId: scr.cameraId,
          snoozeFor: scr.metadata.snoozeDurationSec,
          snoozedAt: scr.metadata.snoozedAt,
        }));
      }

      // For DELETE, return empty array (no more snoozed cameras)
      if (method === 'DELETE') {
        return [];
      }

      // For POST, return void
    } catch (err) {
      throw new Error(String(err));
    }
  }

  async getSnoozeCameras(jwt: string, siteId: SiteId): Promise<SnoozeCamera[]> {
    return (await this.snoozeRequest('GET', jwt, siteId)) as SnoozeCamera[];
  }

  async updateSnoozeCameras(
    jwt: string,
    siteId: SiteId,
    snoozeCameras: SnoozeCamera[],
  ): Promise<void> {
    await this.snoozeRequest(
      'POST',
      jwt,
      siteId,
      snoozeCameras.map(({ snoozedAt, ...rest }) => rest), // Exclude snoozedAt when sending to server
    );
  }

  async deleteSnoozeCameras(jwt: string, siteId: SiteId): Promise<SnoozeCamera[]> {
    return (await this.snoozeRequest('DELETE', jwt, siteId)) as SnoozeCamera[];
  }

  async snoozeAllCameras(jwt: string, siteId: SiteId, snoozeFor: number): Promise<void> {
    try {
      const response = await fetch(
        `${settings.monitorCenterHost}/sites/${siteId}/snoozeAllCameras`,
        {
          method: 'POST',
          headers: this.getDefaultHeaders(jwt),
          body: JSON.stringify({ snoozeFor }),
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to snooze all cameras: ${response.statusText}`);
      }
    } catch (err) {
      throw new Error(String(err));
    }
  }

  private getDefaultHeaders(jwt: string): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    };
  }

  async editAlarmEvent({
    jwt,
    eventId,
    username,
    customerId,
    siteId,
    payload,
    editTemplateId,
  }: {
    jwt: string;
    eventId: EventId;
    username: string;
    customerId: CustomerId;
    siteId: SiteId;
    payload: Record<string, string>[] | Record<string, string>;
    editTemplateId: string;
  }) {
    const body = {
      eventId,
      params: {
        username,
        customerId,
        siteId,
      },
      payload,
    };

    const resp = await fetch(
      `${settings.monitorCenterHost}/verifEye/editAlarmEvent/${editTemplateId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(body),
      },
    );

    if (!resp.ok) {
      throw new Error(`Failed to edit alarm event: ${resp.statusText}`);
    }

    return await resp.json();
  }

  async getVerifEyeSite(
    jwt: string,
    siteId: SiteId,
    cameraIds: CHCameraId[],
  ): Promise<VideoDiscoverySite> {
    const cameraIdsParam = encodeURIComponent(JSON.stringify(cameraIds));
    const resp = await fetch(
      `${settings.monitorCenterHost}/verifEye/getSite/${siteId}?cameraIds=${cameraIdsParam}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
      },
    );

    if (!resp.ok) {
      throw new Error(`Failed to get VerifEye site: ${resp.statusText}`);
    }

    return await resp.json();
  }

  async goToNextEventState({
    jwt,
    eventId,
    eventFlowId,
    customerId,
    payload,
  }: {
    jwt: string;
    eventId: EventId;
    eventFlowId: string;
    customerId: string;
    payload: EventStatePayload;
  }): Promise<EventState> {
    const resp = await fetch(
      `${settings.monitorCenterHost}/verifEye/${eventId}/eventFlows/${eventFlowId}`,
      {
        method: 'POST',
        headers: this.getDefaultHeaders(jwt),
        body: JSON.stringify({ customerId, ...payload }),
      },
    );

    if (!resp.ok) {
      let serverMessage: string | undefined;
      try {
        const body = (await resp.json()) as { message?: string };
        serverMessage = typeof body?.message === 'string' ? body.message : undefined;
      } catch {
        // body not JSON or empty — fall through to statusText
      }
      throw new Error(serverMessage ?? `Failed to advance VerifEye event flow: ${resp.statusText}`);
    }

    return await resp.json();
  }

  // Both halves of the verification record. `props` carries MC's own view of the
  // verification — including the `state` it rejects claims on — which callers
  // cannot derive from the event document alone, since the two can disagree (an
  // expired verification is CLOSED while its event is still OPEN and unverified).
  async getVerification(
    jwt: string,
    eventId: EventId,
  ): Promise<{ event: IEvent; props?: VerificationProps }> {
    const resp = await fetch(`${settings.monitorCenterHost}/verifications/${eventId}`, {
      method: 'GET',
      headers: this.getDefaultHeaders(jwt),
    });

    if (!resp.ok) {
      throw new Error(`Failed to get verification event: ${resp.statusText}`);
    }

    const body: { event: IEvent; props?: VerificationProps } = await resp.json();
    // Match platformClient.getEvent's contract: IEvent.startTime/endTime are
    // typed as Date, so we must coerce the ISO strings from MC's JSON here.
    // Without this, downstream consumers like useVerifEye4Cameras get strings
    // and the alarm video URL is built with start=<ISO string> instead of
    // start=<epoch ms>, which the device rejects.
    return { event: normalizeEvent(body.event), props: body.props };
  }

  async getVerificationEvent(jwt: string, eventId: EventId): Promise<IEvent> {
    const { event } = await this.getVerification(jwt, eventId);
    return event;
  }

  async getVerificationProps(
    jwt: string,
    eventId: EventId,
  ): Promise<VerificationProps | undefined> {
    const resp = await fetch(`${settings.monitorCenterHost}/verifications/${eventId}`, {
      method: 'GET',
      headers: this.getDefaultHeaders(jwt),
    });

    if (!resp.ok) {
      throw new Error(`Failed to get verification props: ${resp.statusText}`);
    }

    const body: { event?: unknown; props?: VerificationProps } = await resp.json();
    return body.props;
  }
}

export default new MonitorCenterApi();
